<script setup lang="ts">
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import { Check } from "lucide-vue-next";

enum PopularPlan {
  NO = 0,
  YES = 1,
}

interface PlanProps {
  title: string;
  popular: PopularPlan;
  price: number;
  description: string;
  buttonText: string;
  benefitList: string[];
}

const plans: PlanProps[] = [
  {
    title: "免费版",
    popular: 0,
    price: 0,
    description:
      "个人用户和小微企业的基础能耗监测解决方案，体验物联网能源管理功能。",
    buttonText: "免费体验",
    benefitList: [
      "最多3个设备接入",
      "基础能耗数据查看",
      "简单设备对比",
      "微信小程序访问",
      "数据存储180天",
      "社区技术支持",
    ],
  },
  {
    title: "专业版",
    popular: 1,
    price: 1980,
    description:
      "中小型企业专业能耗管理平台，提供全面的用电、用水、碳排放监测和分析功能。",
    buttonText: "立即购买",
    benefitList: [
      "最多200台设备接入",
      "能耗历史记录查询",
      "设备异常报警",
      "巡检和报表功能",
      "移动端设定",
      "智能功能和优化建议",
      "一年技术支持",
      "现场安装指导",
      "额外设备接入费：50元/台/年",
    ],
  },
  {
    title: "软件定制",
    popular: 0,
    price: 0,
    description:
      "面向大型企业和工业园区的完整能源管理解决方案，包含硬件集成和专业服务。",
    buttonText: "定制咨询",
    benefitList: [
      "硬件设备一体化集成",
      "专业技术团队服务",
      "系统定制开发",
      "多平台数据对接",
      "企业级安全保障",
      "7×24小时运维支持",
      "专属客户经理",
      "员工培训和认证服务",
    ],
  },
];
</script>

<template>
  <section id="pricing" class="container py-24 sm:py-32">
    <h2 class="text-lg text-primary text-center mb-2 tracking-wider">
      智能化解决方案价格方案
    </h2>

    <h2 class="text-3xl md:text-4xl text-center font-bold mb-4">
      选择适合的
      <span class="text-transparent bg-gradient-to-r from-[#D247BF] to-primary bg-clip-text">
        物联网+AI智能管理方案
      </span>
    </h2>

    <h3
      class="md:w-1/2 mx-auto text-xl text-center text-muted-foreground pb-14"
    >
      从免费体验到专业版本，再到工业自动化企业定制解决方案，满足不同规模企业的智能化管理需求。
    </h3>

    <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-4">
      <Card
        v-for="{
          title,
          popular,
          price,
          description,
          buttonText,
          benefitList,
        } in plans"
        :key="title"
        :class="{
          'drop-shadow-xl shadow-black/10 dark:shadow-white/10 border-[1.5px] border-primary lg:scale-[1.1]':
            popular === PopularPlan?.YES,
        }"
      >
        <CardHeader>
          <CardTitle class="pb-2">
            {{ title }}
          </CardTitle>

          <CardDescription class="pb-4">{{ description }}</CardDescription>

          <div>
            <span v-if="price === 0 && title === '免费版'" class="text-3xl font-bold text-primary">免费</span>
            <span v-else-if="price === 0" class="text-3xl font-bold text-primary">联系报价</span>
            <span v-else class="text-3xl font-bold">¥{{ price.toLocaleString() }}</span>
            <span v-if="price > 0" class="text-muted-foreground"> /年</span>
          </div>
        </CardHeader>

        <CardContent class="flex">
          <div class="space-y-4">
            <span
              v-for="benefit in benefitList"
              :key="benefit"
              class="flex"
            >
              <Check class="text-primary mr-2" />
              <h3>{{ benefit }}</h3>
            </span>
          </div>
        </CardContent>

        <CardFooter>
          <Button
            as-child
            :variant="popular === PopularPlan?.NO ? 'secondary' : 'default'"
            class="w-full"
          >
            <a
              :href="title === '软件定制' ? '#contact' : '#contact'"
              class="w-full text-center"
            >
              {{ buttonText }}
            </a>
          </Button>
        </CardFooter>
      </Card>
    </div>
  </section>
</template>
